// Palette — the editor's insert menu, GENERATED from a template's Shape.
//
// The one idea this component exists to make visible: what an author may insert
// at a position is NOT a hardcoded list of buttons — it is the Shape's insert
// allow-list, rendered (editor-general-vs-constrained.md §3.4, §4; rich-content-
// and-media.md §C.1: one Shape gates element AND inline/media kinds). A rich
// template and a constrained quiz template are the SAME component fed two
// different Shapes; the smaller Shape simply yields a smaller palette. There is
// no per-template `if` anywhere here — the allow-list IS the palette.
//
// Discipline (dev-ts, CLAUDE.md):
//   • The Shape's allow-lists are readonly arrays of NAMED discriminant sets
//     (ElementKind / InlineKind from Editor/types.ts, MediaKind defined here).
//     A kind absent from the Shape is simply not mapped → not rendered.
//   • The insert intent reported UP is a typed discriminated union (InsertIntent),
//     never a bare string blob — a "block" insert and a "media" insert are
//     distinct variants, so the parent narrows on `type`, never guesses from the
//     string's spelling.
//   • kind → author-facing metadata is a TOTAL switch on the discriminant (no
//     cast; a new kind stops the switch compiling until it is handled — the
//     point). Author-facing labels only: "Multiple-choice question", never
//     "mcq" (no dev jargon on user surfaces).
//
// Pure/props-driven: no state, no I/O. `shape` in, grouped insertable items out;
// activating an item calls `onInsert(intent)`. The open/close of a launcher is
// the caller's concern (a Popover trigger owns that state) — see the stories.

import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import type { ElementKind, InlineKind } from "../types.js";
import "./Palette.css";

// The four block-level media kinds (rich-content-and-media.md §B.3). Defined
// locally: the Editor's media types are per-kind payload shapes with no shared
// discriminant to dispatch on yet (see the `=== Media ===` note in types.ts), so
// the palette owns the small tag set it needs to name each insertable media kind.
export type MediaKind = "image" | "video" | "audio" | "embed";

// The insert allow-list a Shape exposes. Three independent axes, each a readonly
// list of a named discriminant set — the single source the palette reads. Two
// Shapes differing only in these arrays yield two different palettes.
export type EditorShape = {
    // Block kinds insertable at the document position (heading, prose, …).
    readonly rootKinds: readonly ElementKind[];
    // Inline formatting the Shape permits inside a block's text (bold, link, …).
    // Latent Shape data: a mark applies to selected text, it is not dropped as a
    // new block, so it is NOT rendered in this insert menu (the earlier
    // non-interactive "Text formatting" note was decoration promising an action
    // the caret engine can't yet deliver — removed 2026-08-19). Consumed by the
    // future slash menu / MarkToolbar once a caret engine mounts them.
    readonly inlineKinds: readonly InlineKind[];
    // Media kinds insertable at the position.
    readonly mediaKinds: readonly MediaKind[];
};

// What the author asked to insert — a typed intent, not a string. `block` and
// `media` are distinct variants so the parent narrows structurally; the payload
// on each is exactly the discriminant set that variant can legally carry.
export type InsertBlock = {
    readonly type: "block";
    readonly kind: ElementKind;
};
export type InsertMedia = { readonly type: "media"; readonly kind: MediaKind };
export type InsertIntent = InsertBlock | InsertMedia;

// One insertable row: the icon + author-facing name + one-line hint the author
// reads, and the typed intent activating it reports up. Built from a kind, never
// authored by hand — so the Shape, not this list, decides what appears.
export type PaletteItem = {
    readonly intent: InsertIntent;
    readonly label: string; // author-facing, localised
    readonly hint: string; // one-line, localised
    readonly icon: ReactNode;
};

export type PaletteProps = {
    readonly shape: EditorShape;
    readonly onInsert: (intent: InsertIntent) => void;
};

// === Icons ====================================================================
// One glyph per insertable kind, from the shared Icon registry — distinct
// silhouettes so the author reads the row by shape as well as by word (colour
// is never the sole channel). Decorative (Icon is aria-hidden by default); the
// row label carries the name.

const headingIcon = <Icon name="heading" />;
const proseIcon = <Icon name="paragraph" />;
const objectiveIcon = <Icon name="objective" />;
const mcqIcon = <Icon name="mcq" />;
const imageIcon = <Icon name="image" />;
const videoIcon = <Icon name="video" />;
const audioIcon = <Icon name="audio" />;
const embedIcon = <Icon name="embed" />;

// === kind → author-facing metadata (total switches) ==========================
// Each is exhaustive over its discriminant set; the trailing `never` binding is
// the compiler's proof that every kind is named — add a variant to ElementKind /
// MediaKind and this stops compiling until the new kind gets author-facing words.
//
// Pure functions return `msg` MessageDescriptors (the diagnosticMessage.ts
// idiom), never localised strings: the earlier version threaded the macro `t`
// through as a runtime argument, which the Lingui macro cannot see — nothing
// was extracted and every label rendered EMPTY. The component localises the
// descriptors with `i18n._` at render.

type ItemMeta = {
    readonly label: MessageDescriptor;
    readonly hint: MessageDescriptor;
    readonly icon: ReactNode;
};

const blockMeta = (kind: ElementKind): ItemMeta => {
    switch (kind) {
        case "heading":
            return {
                label: msg`Heading`,
                hint: msg`A section title`,
                icon: headingIcon,
            };
        case "prose":
            return {
                label: msg`Paragraph`,
                hint: msg`A block of body text`,
                icon: proseIcon,
            };
        case "objective":
            return {
                label: msg`Learning objective`,
                hint: msg`What the learner should be able to do`,
                icon: objectiveIcon,
            };
        case "mcq":
            return {
                label: msg`Multiple-choice question`,
                hint: msg`A question with selectable answers`,
                icon: mcqIcon,
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

const mediaMeta = (kind: MediaKind): ItemMeta => {
    switch (kind) {
        case "image":
            return {
                label: msg`Image`,
                hint: msg`A picture with alt text`,
                icon: imageIcon,
            };
        case "video":
            return {
                label: msg`Video`,
                hint: msg`A video clip with captions`,
                icon: videoIcon,
            };
        case "audio":
            return {
                label: msg`Audio`,
                hint: msg`A sound clip with a transcript`,
                icon: audioIcon,
            };
        case "embed":
            return {
                label: msg`Embed`,
                hint: msg`An external interactive frame`,
                icon: embedIcon,
            };
        default: {
            const exhaustive: never = kind;
            return exhaustive;
        }
    }
};

// === Rendering ================================================================

// One insertable group (Blocks / Media). Rendered only by the parent when it has
// items — so an empty allow-list contributes nothing (progressive disclosure: no
// empty scaffolding). role="group" + `aria-label` (from the heading string, no
// hand-wired ids) names the cluster WITHOUT minting a page landmark — a
// <section aria-label> is a region landmark, and an insert menu's internal
// groups are not page structure (two palettes on screen would also collide on
// axe landmark-unique). Same structural-role reasoning as ProposalCard's
// role="group".
const PaletteSection = ({
    heading,
    items,
    onInsert,
}: {
    readonly heading: string;
    readonly items: readonly PaletteItem[];
    readonly onInsert: (intent: InsertIntent) => void;
}): ReactNode => (
    // biome-ignore lint/a11y/useSemanticElements: the semantic carrier for role="group" is <fieldset> (a form idiom) or <section> (a page LANDMARK) — the landmark is exactly the axe landmark-unique bug this div fixes; a non-landmark named group is only expressible as div[role=group].
    <div className="bilda-palette__group" role="group" aria-label={heading}>
        <Text
            as="div"
            size="xs"
            weight="medium"
            tone="faint"
            className="bilda-palette__heading"
        >
            {heading}
        </Text>
        {items.map((item) => (
            <button
                // The intent's discriminant + kind uniquely names a row.
                key={`${item.intent.type}:${item.intent.kind}`}
                type="button"
                className="bilda-palette__item"
                onClick={() => onInsert(item.intent)}
            >
                <span className="bilda-palette__icon" aria-hidden="true">
                    {item.icon}
                </span>
                <span className="bilda-palette__body">
                    <Text as="span" size="sm" weight="medium">
                        {item.label}
                    </Text>
                    <Text as="span" size="xs" tone="faint">
                        {item.hint}
                    </Text>
                </span>
            </button>
        ))}
    </div>
);

export const Palette = ({ shape, onInsert }: PaletteProps): ReactNode => {
    const { t, i18n } = useLingui();

    // Map each allow-list through its total metadata switch, preserving the
    // Shape's own order. The Shape is the single source — no list is authored
    // here, only projected from what the Shape permits. The pure switches
    // return descriptors; `i18n._` localises them here, at the impure edge.
    const localise = (intent: InsertIntent, meta: ItemMeta): PaletteItem => ({
        intent,
        label: i18n._(meta.label),
        hint: i18n._(meta.hint),
        icon: meta.icon,
    });
    const blockItems: readonly PaletteItem[] = shape.rootKinds.map((kind) =>
        localise({ type: "block", kind }, blockMeta(kind)),
    );
    const mediaItems: readonly PaletteItem[] = shape.mediaKinds.map((kind) =>
        localise({ type: "media", kind }, mediaMeta(kind)),
    );

    return (
        <div className="bilda-palette">
            {blockItems.length > 0 ? (
                <PaletteSection
                    heading={t`Blocks`}
                    items={blockItems}
                    onInsert={onInsert}
                />
            ) : null}
            {mediaItems.length > 0 ? (
                <PaletteSection
                    heading={t`Media`}
                    items={mediaItems}
                    onInsert={onInsert}
                />
            ) : null}
        </div>
    );
};
